import { Checkbox } from '@cian/ui-kit';

import * as React from 'react';

import { type FOfferType, type NonEmptyArray, isCommercial as isCommercialFn } from '@/packages/JsonQuery';
import { EFloorTypes, type IJsonQueryRangeValue } from '@/packages/api-models/common/json_query';

import { useDebouncedCallback } from '../../../utils/useDebouncedCallback';
import { Filter } from '../common/Filter';
import { InlineFilter } from '../common/InlineFilter';
import { InlineFilterGroup } from '../common/InlineFilterGroup';
import { RangeInput } from '../common/RangeInput';

interface IFloorProps {
  offerType: FOfferType;
  floorTypes: NonEmptyArray<EFloorTypes> | null;
  floor: IJsonQueryRangeValue | null;
  isFirstFloor: boolean | null;
  notLastFloor: boolean | null;
  onFloorMinChange(gte: number | null): void;
  onFloorMaxChange(lte: number | null): void;
  onIsFirstFloorChange(isFirstFloor: boolean | null): void;
  onNotLastFloorChange(notLastFloor: boolean | null): void;
  onFloorTypesChange(floorTypes: NonEmptyArray<EFloorTypes> | null): void;
}

export const Floor: React.FC<IFloorProps> = ({
  floor,
  floorTypes,
  offerType,
  isFirstFloor,
  notLastFloor,
  onFloorMinChange,
  onFloorMaxChange,
  onIsFirstFloorChange,
  onNotLastFloorChange,
  onFloorTypesChange,
}) => {
  const { gte: floorMin = null, lte: floorMax = null } = floor || {};
  const isCommercial = isCommercialFn(offerType);
  const isFloorRangeDisabled = floorTypes?.includes(EFloorTypes.OnlyFirstFloor);

  const handleFloorMinChange = useDebouncedCallback(
    (gte: number | null) => {
      onFloorMinChange(gte);
    },
    300,
    [onFloorMinChange],
  );

  const handleFloorMaxChange = useDebouncedCallback(
    (lte: number | null) => {
      onFloorMaxChange(lte);
    },
    300,
    [onFloorMaxChange],
  );

  const handleFloorTypesChange = (event: React.ChangeEvent<HTMLInputElement>, floorType: EFloorTypes): void => {
    const { checked } = event.target;
    let nextValue: EFloorTypes[] = [...(floorTypes || [])];

    if (checked) {
      nextValue.push(floorType);
    } else {
      nextValue = nextValue.filter(v => v !== floorType);
    }

    onFloorTypesChange(nextValue.length > 0 ? (nextValue as NonEmptyArray<EFloorTypes>) : null);
  };

  return (
    <Filter label="Этаж">
      <InlineFilterGroup>
        <InlineFilter>
          <RangeInput
            disabled={isFloorRangeDisabled}
            max={200}
            min={1}
            valueMax={floorMax}
            valueMin={floorMin}
            onChangeMax={handleFloorMaxChange}
            onChangeMin={handleFloorMinChange}
          />
        </InlineFilter>
        {!isCommercial && (
          <>
            <InlineFilter>
              <Checkbox
                checked={isFirstFloor === false}
                label="Не первый"
                onChange={event => onIsFirstFloorChange(event.target.checked ? false : null)}
              />
            </InlineFilter>
            <InlineFilter>
              <Checkbox
                checked={!!notLastFloor}
                label="Не последний"
                onChange={event => onNotLastFloorChange(event.target.checked || null)}
              />
            </InlineFilter>
            <InlineFilter>
              <Checkbox
                checked={notLastFloor === false}
                label="Только последний"
                onChange={event => onNotLastFloorChange(event.target.checked ? false : null)}
              />
            </InlineFilter>
          </>
        )}
        {isCommercial && (
          <>
            <InlineFilter>
              <Checkbox
                checked={floorTypes ? floorTypes.includes(EFloorTypes.OnlyFirstFloor) : false}
                label="Только первый"
                onChange={event => handleFloorTypesChange(event, EFloorTypes.OnlyFirstFloor)}
              />
            </InlineFilter>
            <InlineFilter>
              <Checkbox
                checked={floorTypes ? floorTypes.includes(EFloorTypes.NotBasement) : false}
                label="Не цоколь, не подвал"
                onChange={event => handleFloorTypesChange(event, EFloorTypes.NotBasement)}
              />
            </InlineFilter>
          </>
        )}
      </InlineFilterGroup>
    </Filter>
  );
};
